<template>
  <div
    id="scheduled-grocery-form"
    class="page-content"
  >
    <div class="row">
      <div class="col-12">
        <card-container
          :id="pageId"
          :is-loading="scheduledGroceryStatus.isLoading"
          :loading-message="scheduledGroceryStatus.loadingMessage"
          button-location="top"
          @submit="onSubmitForm"
        >
          <template #title>
            {{ scheduledGroceryData.id ? 'Update Scheduled Grocery' : 'Create Scheduled Grocery' }}
          </template>
          <template #content>
            <el-steps
              :active="scheduledGroceryStep"
              finish-status="success"
              align-center
            >
              <el-step title="Store Info" />
              <el-step title="Items Info" />
              <el-step title="Schedule Info" />
            </el-steps>
            <div class="mt-20">
              <scheduled-grocery-form-step-one v-show="scheduledGroceryStep === 0" />
              <scheduled-grocery-form-step-two v-show="scheduledGroceryStep === 1" />
              <scheduled-grocery-form-step-three v-show="scheduledGroceryStep === 2" />
            </div>
          </template>
        </card-container>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import { onBeforeUnmount, watch, defineAsyncComponent } from 'vue';
import _ from 'lodash';

// User-defined components
import util from '@/Utils/util';
import { ScheduledGrocerySteps } from '@/models/grocery';
import { useSwalDialog } from '@/use/swalDialog';
import { useGrocery } from '@/use/grocery';
import { useCustomer } from '@/use/customer';
import { ScheduledGroceryInput } from '@/models/scheduledGrocery';
import { scheduledGroceryService } from '@/services/scheduledGrocery';

const ScheduledGroceryFormStepOne = defineAsyncComponent(() => import('./ScheduledGroceryFormStepOne.vue') as any);

const ScheduledGroceryFormStepTwo = defineAsyncComponent(() => import('./ScheduledGroceryFormStepTwo.vue') as any);

const ScheduledGroceryFormStepThree = defineAsyncComponent(() => import('./ScheduledGroceryFormStepThree.vue') as any);

const pageId = 'scheduled-grocery-form';

const router = useRouter();
const { customerPhoneNumber } = useCustomer();
const {
  scheduledGroceryData,
  scheduledGroceryStep,
  scheduledGroceryStatus,
  scheduledGroceryEstimate,
  setScheduledGroceryStatus,
  resetScheduledGroceryData,
} = useGrocery();
const { showErrorPopupDialog, showSuccessPopupDialog } = useSwalDialog();

const getData = (): ScheduledGroceryInput => {
  return {
    ..._.omit(scheduledGroceryData.value, ['endOnType']),
    tip: scheduledGroceryData.value.tip ? Number(scheduledGroceryData.value.tip) : 0,
    store: {
      storeId: scheduledGroceryData.value.store.storeId,
      name: scheduledGroceryData.value.store.name,
      slug: scheduledGroceryData.value.store.slug,
      logo: scheduledGroceryData.value.store.logo,
    },
    scheduleTime: util.formatDateTime(scheduledGroceryData.value.scheduleTime, 'HH:mm'),
    scheduleDate: util.formatDateTime(scheduledGroceryData.value.scheduleDate, 'YYYY-MM-DD'),
    endOn:
      scheduledGroceryData.value.isMultiple && scheduledGroceryData.value.endOnType === 'on'
        ? util.formatDateTime(scheduledGroceryData.value.endOn, 'YYYY-MM-DD')
        : null,
    endAfterXTimes:
      scheduledGroceryData.value.isMultiple && scheduledGroceryData.value.endOnType === 'after' && scheduledGroceryData.value.endAfterXTimes
        ? Number(scheduledGroceryData.value.endAfterXTimes)
        : null,
  };
};

const createScheduledGrocery = async () => {
  setScheduledGroceryStatus({
    isLoading: true,
    loadingMessage: 'Creating Scheduled Grocery...',
  });

  const data = getData();

  try {
    await scheduledGroceryService.createScheduledGrocery(data);

    showSuccessPopupDialog({
      text: 'The scheduled grocery has been created successfully.',
    });
    resetScheduledGroceryData();

    router.push({
      name: 'ScheduledGrocery',
      query: {
        phoneNumber: customerPhoneNumber.value,
      },
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    setScheduledGroceryStatus({
      isLoading: false,
      loadingMessage: '',
    });
  }
};

const updateScheduledGrocery = async () => {
  setScheduledGroceryStatus({
    isLoading: true,
    loadingMessage: 'Updating Scheduled Grocery...',
  });

  const data = getData();

  try {
    await scheduledGroceryService.updateScheduledGrocery(data);

    showSuccessPopupDialog({
      text: 'The scheduled grocery has been updated successfully.',
    });
    resetScheduledGroceryData();

    router.push({
      name: 'ScheduledGrocery',
      query: {
        phoneNumber: customerPhoneNumber.value,
      },
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    setScheduledGroceryStatus({
      isLoading: false,
      loadingMessage: '',
    });
  }
};

const getScheduledGroceryEstimate = async () => {
  scheduledGroceryEstimate.value = null;
  setScheduledGroceryStatus({
    isLoading: true,
    loadingMessage: 'Get Estimate...',
  });

  const data = getData();

  try {
    scheduledGroceryEstimate.value = await scheduledGroceryService.getScheduledGroceryEstimate(data);
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    setScheduledGroceryStatus({
      isLoading: false,
      loadingMessage: '',
    });
  }
};

const onSubmitForm = () => {
  switch (scheduledGroceryStep.value) {
    case ScheduledGrocerySteps.StoreInfo:
      scheduledGroceryStep.value = ScheduledGrocerySteps.ItemInfo;
      break;
    case ScheduledGrocerySteps.ItemInfo:
      scheduledGroceryStep.value = ScheduledGrocerySteps.ScheduleInfo;
      break;
    case ScheduledGrocerySteps.ScheduleInfo:
      if (!scheduledGroceryEstimate.value) {
        getScheduledGroceryEstimate();
      } else if (scheduledGroceryData.value.id) {
        updateScheduledGrocery();
      } else {
        createScheduledGrocery();
      }
      break;
  }
};

watch(
  scheduledGroceryData.value,
  () => {
    if (scheduledGroceryEstimate.value) {
      scheduledGroceryEstimate.value = null;
    }
  },
  {
    immediate: true,
  },
);

onBeforeUnmount(resetScheduledGroceryData);

util.moveToTop();
</script>
